import { LanguagePicker, Select, useI18n } from '@upweo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../lib/auth-context.js';

/**
 * Admin shell: fixed sidebar, sticky top bar, scrolling content.
 *
 * A sidebar rather than top nav because the section list grows — orders,
 * customers, discounts, analytics, settings — and horizontal nav runs out of
 * room and starts collapsing into a hamburger exactly when the app gets useful.
 */
/**
 * `as const` matters here: without it `labelKey` widens to `string` and the
 * translator's key type stops catching typos — which is most of the value of
 * having typed keys at all.
 */
const NAV = [
  {
    to: '/products',
    labelKey: 'nav.products',
    icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  },
  { to: '/orders', labelKey: 'nav.orders', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
  {
    to: '/customers',
    labelKey: 'nav.customers',
    icon: 'M17 20h5v-2a3 3 0 00-5.36-1.87M17 20H7m10 0v-2c0-.66-.13-1.3-.36-1.87M7 20H2v-2a3 3 0 015.36-1.87M7 20v-2c0-.66.13-1.3.36-1.87m0 0a5 5 0 019.28 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  },
  {
    to: '/settings',
    labelKey: 'nav.settings',
    icon: 'M10.3 4.3a1 1 0 011.4 0l1 1a1 1 0 00.9.3l1.4-.2a1 1 0 011.1.7l.4 1.3a1 1 0 00.6.7l1.3.5a1 1 0 01.6 1.2l-.3 1.4a1 1 0 00.2.9l.9 1.1a1 1 0 010 1.3l-.9 1.1a1 1 0 00-.2.9l.3 1.4a1 1 0 01-.6 1.2l-1.3.5a1 1 0 00-.6.7l-.4 1.3a1 1 0 01-1.1.7l-1.4-.2a1 1 0 00-.9.3l-1 1a1 1 0 01-1.4 0l-1-1a1 1 0 00-.9-.3l-1.4.2a1 1 0 01-1.1-.7l-.4-1.3a1 1 0 00-.6-.7l-1.3-.5a1 1 0 01-.6-1.2l.3-1.4a1 1 0 00-.2-.9l-.9-1.1a1 1 0 010-1.3l.9-1.1a1 1 0 00.2-.9l-.3-1.4a1 1 0 01.6-1.2l1.3-.5a1 1 0 00.6-.7l.4-1.3a1 1 0 011.1-.7l1.4.2a1 1 0 00.9-.3zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  },
] as const;

export const AppLayout = (): React.JSX.Element => {
  const { user, activeStore, signOut, selectStore } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const onStoreChange = (tenantId: string): void => {
    void (async () => {
      await selectStore(tenantId);
      /**
       * Every cached query holds data for the PREVIOUS store. Clearing rather
       * than refetching guarantees no stale tenant's rows can flash on screen
       * while the new request is in flight.
       */
      await queryClient.resetQueries();
    })();
  };

  /**
   * Initials from the name, falling back to the email. Written as an explicit
   * empty check rather than `||` because the fallback is triggered by an EMPTY
   * STRING (a user with no first or last name), which `??` would not catch.
   */
  const nameInitials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('');
  const initials =
    nameInitials === '' ? (user?.email.slice(0, 2).toUpperCase() ?? '?') : nameInitials;

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[15rem_1fr]">
      <aside className="hidden border-r bg-card lg:flex lg:flex-col">
        <div className="flex h-14 items-center gap-2 border-b px-5">
          <div className="flex h-6 w-6 items-center justify-center rounded bg-primary text-[11px] font-bold text-primary-foreground">
            U
          </div>
          <span className="text-sm font-semibold tracking-tight">Upweo</span>
        </div>

        <nav className="flex-1 space-y-0.5 p-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 shrink-0"
                aria-hidden="true"
              >
                <path d={item.icon} />
              </svg>
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="border-t p-3">
          <div className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              {/* truncate: a long email must not push the sign-out link off-screen */}
              <p className="truncate text-xs font-medium">{user?.email}</p>
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                {t('common.signOut')}
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b bg-background/85 px-5 backdrop-blur">
          <span className="text-sm font-semibold tracking-tight lg:hidden">Upweo</span>

          {user && user.memberships.length > 0 && (
            <label className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('common.store')}</span>
              <Select
                aria-label={t('common.store')}
                value={activeStore?.tenantId ?? ''}
                onChange={(e) => {
                  onStoreChange(e.target.value);
                }}
                className="h-8 w-auto min-w-44 text-sm"
              >
                {user.memberships.map((m) => (
                  <option key={m.tenantId} value={m.tenantId}>
                    {m.tenantName}
                  </option>
                ))}
              </Select>
            </label>
          )}

          {activeStore && (
            <span className="hidden rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground sm:inline">
              {activeStore.role.toLowerCase()} · {activeStore.currencyCode}
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            <LanguagePicker compact />
          </div>

          <nav className="flex items-center gap-1 lg:hidden">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-2.5 py-1.5 text-sm ${
                    isActive
                      ? 'bg-accent font-medium text-accent-foreground'
                      : 'text-muted-foreground'
                  }`
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="flex-1 px-5 py-6 lg:px-8">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
